import React, { useState } from 'react';

import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';

import AppModal from '../../../ui-components/AppModal';
import { useChangePasswordMutation } from './changePasswordApi';

import type { ChangePasswordRequest } from './model';

const REQUIRED_FIELD_MSG = 'Please enter a value for the field.';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [changePassword] = useChangePasswordMutation();
  const [showPass, setShowPass] = useState({
    oldPass: false,
    newPass: false,
    confirmPass: false,
  });

  const toggleShow = (key: keyof typeof showPass) => {
    setShowPass((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordRequest>();

  const handleClose = () => {
    reset();
    setShowPass({ oldPass: false, newPass: false, confirmPass: false });
    onClose();
  };

  const onSubmit = async (values: ChangePasswordRequest) => {
    try {
      await changePassword(values).unwrap();
      reset();
      onSuccess?.();
      onClose();
    } catch (err) {
      // Error is handled globally in baseQuery
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      title="Change Password"
      description="Enter your current password and choose a new one"
      onClose={handleClose}
      hideFooter
      variant="form"
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="currentPassword"
              className="text-xs text-neutral-700 dark:text-neutral-300 block mb-1.5"
            >
              Current Password <span className="text-error-500">*</span>
            </label>
            <div className="relative">
              <input
                id="currentPassword"
                type={showPass.oldPass ? 'text' : 'password'}
                maxLength={15}
                onCopy={(event) => event.preventDefault()}
                onCut={(event) => event.preventDefault()}
                autoFocus
                placeholder="Enter current password"
                className="w-full h-10 px-3 pr-10 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-[8px] text-sm text-neutral-900 dark:text-white placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                {...register('currentPassword', {
                  required: REQUIRED_FIELD_MSG,
                })}
              />
              <button
                type="button"
                onClick={() => toggleShow('oldPass')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                aria-label={
                  showPass.oldPass ? 'Hide password' : 'Show password'
                }
              >
                {showPass.oldPass ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="text-xs text-error-500 mt-1">
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="newPassword"
              className="text-xs text-neutral-700 dark:text-neutral-300 block mb-1.5"
            >
              New Password <span className="text-error-500">*</span>
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showPass.newPass ? 'text' : 'password'}
                maxLength={15}
                placeholder="Enter new password"
                className="w-full h-10 px-3 pr-10 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-[8px] text-sm text-neutral-900 dark:text-white placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                {...register('newPassword', {
                  required: REQUIRED_FIELD_MSG,
                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,15}$/,
                    message:
                      'Password must be 6-15 characters with 1 uppercase, 1 lowercase, 1 number & 1 special character (!@#$%^&*)',
                  },
                })}
              />
              <button
                type="button"
                onClick={() => toggleShow('newPass')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                aria-label={
                  showPass.newPass ? 'Hide password' : 'Show password'
                }
              >
                {showPass.newPass ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Must be at least 8 characters long
            </p>
            {errors.newPassword && (
              <p className="text-xs text-error-500 mt-1">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmNewPassword"
              className="text-xs text-neutral-700 dark:text-neutral-300 block mb-1.5"
            >
              Confirm New Password <span className="text-error-500">*</span>
            </label>
            <div className="relative">
              <input
                id="confirmNewPassword"
                type={showPass.confirmPass ? 'text' : 'password'}
                maxLength={15}
                placeholder="Confirm new password"
                className="w-full h-10 px-3 pr-10 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-[8px] text-sm text-neutral-900 dark:text-white placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                {...register('confirmNewPassword', {
                  required: REQUIRED_FIELD_MSG,
                  validate: (value: string) =>
                    value === watch('newPassword') || 'Passwords must match',
                })}
              />
              <button
                type="button"
                onClick={() => toggleShow('confirmPass')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                aria-label={
                  showPass.confirmPass ? 'Hide password' : 'Show password'
                }
              >
                {showPass.confirmPass ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirmNewPassword && (
              <p className="text-xs text-error-500 mt-1">
                {errors.confirmNewPassword.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-4 border-t border-neutral-200 dark:border-neutral-800">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Change Password
          </button>
        </div>
      </form>
    </AppModal>
  );
};

export default ChangePasswordModal;
